/**
 * Tipos y constantes del dominio.
 *
 * Este modulo es deliberadamente puro: no importa nada de Node ni de la base de
 * datos, de modo que el panel web puede reutilizar los mismos tipos y etiquetas
 * sin arrastrar codigo de servidor.
 */

// --- Conversaciones ----------------------------------------------------------

export const CONVERSATION_STATUSES = ['open', 'pending', 'resolved', 'snoozed'] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

export const CONVERSATION_STATUS_LABELS: Record<ConversationStatus, string> = {
  open: 'Abierta',
  pending: 'Pendiente',
  resolved: 'Resuelta',
  snoozed: 'Pospuesta',
};

/**
 * Controla quien responde en una conversacion:
 *  - `auto`   el agente de IA responde y puede escalar a un humano
 *  - `paused` un humano ha tomado el control; la IA permanece en silencio
 *  - `off`    la IA esta desactivada para esta conversacion (solo humanos)
 */
export const BOT_MODES = ['auto', 'paused', 'off'] as const;
export type BotMode = (typeof BOT_MODES)[number];

// --- Mensajes ----------------------------------------------------------------

export const MESSAGE_DIRECTIONS = ['inbound', 'outbound'] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number];

export const MESSAGE_TYPES = [
  'text',
  'image',
  'audio',
  'video',
  'document',
  'sticker',
  'location',
  'contacts',
  'interactive',
  'button',
  'template',
  'reaction',
  'system',
  'unsupported',
] as const;
export type MessageType = (typeof MESSAGE_TYPES)[number];

/**
 * Ciclo de vida de un mensaje.
 * `received` solo aplica a entrantes; el resto describe el recorrido de salida.
 */
export const MESSAGE_STATUSES = [
  'received',
  'queued',
  'sending',
  'sent',
  'delivered',
  'read',
  'failed',
] as const;
export type MessageStatus = (typeof MESSAGE_STATUSES)[number];

/** Quien escribio el mensaje. */
export const AUTHOR_TYPES = ['contact', 'user', 'bot', 'system'] as const;
export type AuthorType = (typeof AUTHOR_TYPES)[number];

// --- Organizaciones y agentes ------------------------------------------------

export const MEMBER_ROLES = ['owner', 'admin', 'agent'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Propietario',
  admin: 'Administrador',
  agent: 'Agente',
};

/** Roles con permiso para cambiar la configuracion de la organizacion. */
export const ADMIN_ROLES: readonly MemberRole[] = ['owner', 'admin'];

export const ACCOUNT_STATUSES = ['active', 'disabled'] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];

// --- Escalado a humano y estado del agente de IA -----------------------------

/**
 * Motivos por los que la IA deja de responder y pasa la conversacion a una
 * persona. Se guardan para poder medir por que se escalan las conversaciones.
 */
export const HANDOFF_REASONS = [
  'customer_request',
  'low_confidence',
  'repeated_failure',
  'sensitive_topic',
  'tool_error',
  'policy_limit',
  'outside_hours',
  'manual',
] as const;
export type HandoffReason = (typeof HANDOFF_REASONS)[number];

export const HANDOFF_REASON_LABELS: Record<HandoffReason, string> = {
  customer_request: 'El cliente pidio una persona',
  low_confidence: 'La IA no estaba segura',
  repeated_failure: 'Intentos repetidos sin exito',
  sensitive_topic: 'Tema sensible (facturacion, quejas, legal)',
  tool_error: 'Fallo de una herramienta interna',
  policy_limit: 'Limite de la politica de atencion',
  outside_hours: 'Fuera del horario de atencion',
  manual: 'Escalado manual por un agente',
};

/** Resultado de una ejecucion del agente de IA (observabilidad). */
export const AI_OUTCOMES = ['replied', 'handoff', 'skipped', 'error'] as const;
export type AiOutcome = (typeof AI_OUTCOMES)[number];

// --- Contactos ---------------------------------------------------------------

export const OPT_IN_STATUSES = ['unknown', 'opted_in', 'opted_out'] as const;
export type OptInStatus = (typeof OPT_IN_STATUSES)[number];

// --- Reglas de negocio -------------------------------------------------------

/**
 * Ventana de atencion al cliente de WhatsApp: se puede responder con mensajes
 * libres durante las 24 h siguientes al ultimo mensaje del cliente. Pasado ese
 * plazo solo se pueden enviar plantillas previamente aprobadas por Meta.
 */
export const CUSTOMER_SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;

/** WhatsApp acepta como maximo 4096 caracteres en un mensaje de texto. */
export const MAX_TEXT_LENGTH = 4096;
